import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { 
  Eye, 
  EyeSlash, 
  Buildings, 
  EnvelopeSimple, 
  Lock,
  ArrowRight,
  HardHat,
  CheckCircle,
  ShieldCheck,
  Lightning
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Demo credentials
const DEMO_EMAIL = 'demo@devisauto.fr';
const DEMO_PASSWORD = 'demo123';

export default function Login() {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company_name: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      const subscriptionStatus = response.data.user?.subscription_status;
      
      if (isRegister) {
        toast.success('Compte créé !');
        navigate('/subscription');
      } else if (subscriptionStatus === 'blocked') {
        navigate('/subscription');
      } else {
        toast.success('Bienvenue');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const fillDemoCredentials = () => {
    setFormData({
      ...formData,
      email: DEMO_EMAIL,
      password: DEMO_PASSWORD
    });
    toast.success('Identifiants démo remplis');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex overflow-hidden">
      <Toaster position="top-center" richColors />
      
      {/* Left - Hero Image (Desktop only) */}
      <div className="hidden lg:flex lg:w-3/5 relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1644411813513-ad77c1b77581?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920)',
          }}
        />
        
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/50" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Top - Logo & Brand */}
          <div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 p-2 flex items-center justify-center">
                <img 
                  src="/angela-logo-clean.png" 
                  alt="Angela" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Angela</h1>
                <p className="text-amber-400 font-medium">Devis intelligents pour le BTP</p>
              </div>
            </div>
          </div>
          
          {/* Middle - Features */}
          <div className="max-w-lg">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl font-bold text-white leading-tight mb-6"
            >
              Créez vos devis en
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500"> quelques minutes</span>
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-slate-400 text-lg mb-8"
            >
              L'IA analyse vos demandes et génère des devis professionnels conformes aux normes 2026.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Lightning size={20} className="text-amber-400" weight="fill" />
                </div>
                <span>Génération IA en 30 secondes</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <ShieldCheck size={20} className="text-emerald-400" weight="fill" />
                </div>
                <span>Conforme e-facture 2026</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <HardHat size={20} className="text-blue-400" weight="fill" />
                </div>
                <span>Conçu pour les artisans du BTP</span>
              </div>
            </motion.div>
          </div>
          
          {/* Bottom - Stats */}
          <div className="flex gap-12">
            <div>
              <p className="text-4xl font-bold text-white">2 500+</p>
              <p className="text-slate-500">Artisans actifs</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white">15 000+</p>
              <p className="text-slate-500">Devis générés</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white">98%</p>
              <p className="text-slate-500">Satisfaction</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right - Form */}
      <div className="w-full lg:w-2/5 flex items-center justify-center p-6 bg-white dark:bg-slate-900 lg:rounded-l-[3rem]">
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-sm"
        >
          {/* Mobile Logo */}
          <div className="flex justify-center mb-8 lg:hidden">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 p-2 flex items-center justify-center">
              <img 
                src="/angela-logo-clean.png" 
                alt="Angela Devis" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {isRegister ? 'Créer un compte' : 'Connexion'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {isRegister ? 'Essai gratuit 30 jours' : 'Accédez à votre espace pro'}
            </p>
          </div>

          {/* Demo Button */}
          {!isRegister && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              type="button"
              onClick={fillDemoCredentials}
              className="w-full mb-6 p-4 rounded-2xl border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
            >
              <div className="flex items-center justify-center gap-3">
                <HardHat size={24} className="text-amber-600" weight="duotone" />
                <div className="text-left">
                  <p className="font-semibold text-amber-800 dark:text-amber-300">Compte Démo</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">Cliquez pour tester l'application</p>
                </div>
              </div>
            </motion.button>
          )}

          {/* Divider */}
          {!isRegister && (
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">ou connectez-vous</span>
              <div className="flex-1 h-px bg-border" />
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="relative">
                <Buildings 
                  size={20} 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" 
                />
                <Input
                  type="text"
                  placeholder="Nom de l'entreprise"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="h-12 pl-12 rounded-xl bg-muted/50 border-0 focus:ring-2 focus:ring-amber-500"
                  required={isRegister}
                />
              </div>
            )}

            <div className="relative">
              <EnvelopeSimple 
                size={20} 
                className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" 
              />
              <Input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-12 pl-12 rounded-xl bg-muted/50 border-0 focus:ring-2 focus:ring-amber-500"
                required
              />
            </div>

            <div className="relative">
              <Lock 
                size={20} 
                className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" 
              />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Mot de passe"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="h-12 pl-12 pr-12 rounded-xl bg-muted/50 border-0 focus:ring-2 focus:ring-amber-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-50 shadow-lg shadow-amber-500/25"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <span>{isRegister ? 'Créer mon compte' : 'Se connecter'}</span>
                  <ArrowRight size={20} weight="bold" />
                </>
              )}
            </button>
          </form>

          {/* Toggle */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            {isRegister ? 'Déjà inscrit ?' : 'Pas encore de compte ?'}
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="ml-2 text-amber-600 hover:text-amber-700 font-semibold"
            >
              {isRegister ? 'Se connecter' : "S'inscrire gratuitement"}
            </button>
          </p>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-6 mt-8 pt-6 border-t border-border">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle size={16} className="text-emerald-500" weight="fill" />
              <span>SSL sécurisé</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle size={16} className="text-emerald-500" weight="fill" />
              <span>RGPD</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
